import type { MacroRecorderCaptureSource, PlaycastMessage, PlaycastMessageTarget, PlaycastMessageStateReplication } from '@playcastdotio/Messaging';
import type { PlaycastEnumRunningApplications } from '@playcastdotio/CoreTypes';

const windowSelectionTimeoutMs = 65_000; // Native process discovery allows 60 seconds.
const inventoryRefreshTimeoutMs = 3_000;
const cameraRefreshTimeoutMs = 3_000;
type WindowCaptureSource = Extract<MacroRecorderCaptureSource, { kind: 'window' }>;
type DisplayCaptureSource = Extract<MacroRecorderCaptureSource, { kind: 'display' }>;

export interface MacroRecorderDisplaySource {
  displayId: number;
  displayName: string;
  width: number;
  height: number;
  x: number;
  y: number;
  isPrimary: boolean;
}

export interface MacroRecorderCaptureSourceFunctions {
  changeDisplay(displayId: number): void;
  processDisconnect(): void;
  processEnumRunning(): void;
  processSelectWindow(processId: number, windowHandle?: string): void;
  setDisplaySurfing(enabled: boolean): void;
  setOutboundVideoDevice(deviceId: string, windowCapturePid?: number): void;
}

export interface MacroRecorderCaptureSourceSnapshot {
  selectedDevice: string;
  selectedDisplayId: number;
  displays: MacroRecorderDisplaySource[];
  windowCapturePid?: number;
  runningApps: Array<{ name: string; processId: number; windowHandle?: string }>;
  cameras: Array<{ deviceId: string; displayName: string }>;
}

export interface MacroRecorderCaptureSourceManagerDeps {
  functions: MacroRecorderCaptureSourceFunctions;
  setPreviewCameraSurfing(enabled: boolean): void;
  getSnapshot(): MacroRecorderCaptureSourceSnapshot;
  refreshCameras(): Promise<void>;
  onSourceSelected?: (source: MacroRecorderCaptureSource) => Promise<void>;
}

/** Studio source inventory with independent, non-foreground recorder window capture. */
export class MacroRecorderCaptureSourceManager {
  private selecting = false;
  private selectedSource: MacroRecorderCaptureSource | null = null;
  private confirmedWindowSource: WindowCaptureSource | null = null;
  private inventoryRequest: Promise<{ sources: MacroRecorderCaptureSource[]; selectedId: string }> | null = null;
  private pendingInventory: ((applications: PlaycastEnumRunningApplications) => void) | null = null;
  private pendingWindowSelection: { resolve(): void; reject(error: Error): void; source: WindowCaptureSource } | null = null;
  public constructor(private readonly deps: MacroRecorderCaptureSourceManagerDeps) {}

  public listSources(): Promise<{ sources: MacroRecorderCaptureSource[]; selectedId: string }> {
    this.inventoryRequest ??= this.refreshInventory().finally(() => { this.inventoryRequest = null; });
    return this.inventoryRequest;
  }

  private async refreshInventory(): Promise<{ sources: MacroRecorderCaptureSource[]; selectedId: string }> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const windows = new Promise<PlaycastEnumRunningApplications>((resolve) => {
        this.pendingInventory = resolve;
        timer = setTimeout(() => {
          const refreshedApplications = this.deps.getSnapshot().runningApps;
          resolve(refreshedApplications.map((application) => [
            application.name,
            { processId: application.processId, screenshot: '',
              ...(application.windowHandle ? { windowHandle: application.windowHandle } : {}) },
          ]));
        }, inventoryRefreshTimeoutMs);
        this.deps.functions.processEnumRunning();
      });
      const [applications] = await Promise.all([windows, this.refreshCamerasWithinBudget()]);
      return this.buildInventory({
        ...this.deps.getSnapshot(),
        // Use the received inventory: legacy state replication may not have applied yet.
        runningApps: applications.map(([name, app]) => ({ name, processId: app.processId, windowHandle: app.windowHandle })),
      });
    } finally {
      clearTimeout(timer);
      this.pendingInventory = null;
    }
  }

  private async refreshCamerasWithinBudget(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        this.deps.refreshCameras(),
        new Promise<void>((resolve) => { timer = setTimeout(resolve, cameraRefreshTimeoutMs); }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  public async selectSource(source: MacroRecorderCaptureSource): Promise<string> {
    if (this.selecting) throw new Error('A capture source change is already in progress');
    this.selecting = true;
    this.selectedSource = null;
    this.confirmedWindowSource = null;
    try {
      await this.applySource(source);
      await this.deps.onSourceSelected?.(source);
      if (source.kind === 'window' && this.confirmedWindowSource !== source)
        throw new Error('The selected window is no longer confirmed. Retry the selection.');
      // The recorder camera is coordinator-owned and may not appear in the
      // legacy active-camera list used by setDisplaySurfing. Configure it
      // explicitly after preview activation and the desktop source refresh.
      this.deps.setPreviewCameraSurfing(source.kind === 'surf');
      this.selectedSource = source;
      return source.id;
    } finally {
      this.selecting = false;
    }
  }

  /** Receives native acknowledgements before publishing a selected window. */
  public handlePlayjectorMessage(message: PlaycastMessage<PlaycastMessageTarget>): void {
    if (message.header.target === 'process' && message.header.action === 'enumRunning' && message.header.isReply) {
      const applications = (message.body.message as { applications?: PlaycastEnumRunningApplications }).applications;
      if (Array.isArray(applications)) this.pendingInventory?.(applications);
      return;
    }
    // Electron publishes every enumRunning reply through this replicated channel.
    if (message.header.target === 'state' && message.header.action === 'replication' && !message.header.isReply) {
      const { channel, state } = (message as PlaycastMessage<PlaycastMessageStateReplication<string>>).body.message;
      if (channel === 'runningApplicationState' && Array.isArray(state)) {
        this.pendingInventory?.(state as PlaycastEnumRunningApplications);
      }
      return;
    }
    if (message.header.target !== 'process' || !message.header.isReply) return;
    const { action } = message.header;
    const { status } = message.body.message as { status?: string };
    if (action === 'disconnect' || (action === 'gameStatus' && (status === 'window closed' || status === 'game closed'))) {
      this.confirmedWindowSource = null;
      return;
    }
    if (action !== 'select') return;
    this.handleWindowSelectionReply(message.body.message as { result?: string; captureMode?: string; windowHandle?: string });
  }

  private handleWindowSelectionReply({ result, captureMode, windowHandle }: {
    result?: string; captureMode?: string; windowHandle?: string;
  }): void {
    const pending = this.pendingWindowSelection;
    if (!pending) {
      if (result === 'success' && (captureMode !== 'window' || !this.confirmedWindowSource?.windowHandle
        || windowHandle !== this.confirmedWindowSource.windowHandle)) this.confirmedWindowSource = null;
      return;
    }
    if (result === 'success' && pending.source.windowHandle && pending.source.windowHandle !== windowHandle) {
      this.confirmedWindowSource = null;
      pending.reject(new Error('The Host did not confirm the exact selected window. Update Playjector and retry.'));
    } else if (result === 'success' && captureMode === 'window') {
      this.confirmedWindowSource = pending.source;
      pending.resolve();
    } else {
      this.confirmedWindowSource = null;
      pending.reject(new Error(result === 'success'
        ? 'This Host build does not support independent window capture. Update Playjector and retry.'
        : result === 'WRONG_MODE'
          ? 'Window capture requires Beacon mode, but the selected Host is in Pavilion mode. Use Display capture or switch that Host to Beacon mode.'
          : `Window capture failed: ${result || 'invalid Playjector reply'}`));
    }
  }

  private async applySource(source: MacroRecorderCaptureSource): Promise<void> {
    switch (source.kind) {
      case 'display':
        this.selectDisplay(source);
        break;
      case 'surf':
        this.selectDesktop(true, 'followMouse');
        break;
      case 'window':
        await this.selectWindow(source);
        break;
      case 'camera':
        this.deps.functions.setDisplaySurfing(false);
        this.deps.functions.processDisconnect();
        this.deps.functions.setOutboundVideoDevice(source.id);
        break;
    }
  }

  private selectDesktop(surfing: boolean, deviceId: 'display' | 'followMouse'): void {
    this.deps.functions.setDisplaySurfing(surfing);
    this.deps.functions.processDisconnect();
    this.deps.functions.setOutboundVideoDevice(deviceId);
  }

  private selectDisplay(source: DisplayCaptureSource): void {
    if (source.displayId === undefined)
      throw new Error('This display is missing its native PlayJector display ID. Refresh capture sources and retry.');
    this.deps.functions.setDisplaySurfing(false);
    this.deps.functions.processDisconnect();
    // Native process teardown clears DMWrapper picking. Apply the display only
    // after teardown so this selection remains authoritative for preflight.
    this.deps.functions.changeDisplay(source.displayId);
    this.deps.functions.setOutboundVideoDevice('display');
  }

  private async selectWindow(source: WindowCaptureSource): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await new Promise<void>((resolve, reject) => {
        this.pendingWindowSelection = { resolve, reject, source };
        timer = setTimeout(() => reject(new Error('Playjector did not confirm the selected window')), windowSelectionTimeoutMs);
        this.deps.functions.setDisplaySurfing(false);
        this.deps.functions.processSelectWindow(source.processId, source.windowHandle);
      });
    } finally {
      clearTimeout(timer);
      this.pendingWindowSelection = null;
    }
    if (this.confirmedWindowSource !== source)
      throw new Error('The selected window is no longer confirmed. Retry the selection.');
    this.deps.functions.setOutboundVideoDevice('gameCapture', source.processId);
  }

  private buildInventory(snapshot: MacroRecorderCaptureSourceSnapshot): {
    sources: MacroRecorderCaptureSource[];
    selectedId: string;
  } {
    const displaySources: DisplayCaptureSource[] = snapshot.displays.map((display, index) => {
      const displayNumber = display.displayName.match(/DISPLAY\s*(\d+)/i)?.[1] ?? `${index + 1}`;
      const nativeName = display.displayName.match(/^(?:\\\\\.\\)?(DISPLAY\d+)$/i)?.[1];
      return {
        kind: 'display',
        id: `display:${display.displayId}`,
        label: `Display ${displayNumber} · ${display.width}×${display.height}${display.isPrimary ? ' · Primary' : ''}`,
        displayId: display.displayId,
        ...(nativeName ? { gdiDeviceName: `\\\\.\\${nativeName.toUpperCase()}` } : {}),
        width: display.width,
        height: display.height,
        x: display.x,
        y: display.y,
        isPrimary: display.isPrimary,
      };
    });
    const sources: MacroRecorderCaptureSource[] = [
      ...displaySources,
      { kind: 'surf', id: 'followMouse', label: 'Surf Desktop' },
      ...snapshot.runningApps.map((app) => ({
        kind: 'window' as const,
        id: app.windowHandle ? `window:${app.processId}:${app.windowHandle}` : `window:${app.processId}`,
        label: app.name,
        processId: app.processId,
        ...(app.windowHandle ? { windowHandle: app.windowHandle } : {}),
      })),
      ...snapshot.cameras.map((camera) => ({
        kind: 'camera' as const,
        id: camera.deviceId,
        label: camera.displayName,
      })),
    ];
    const confirmedWindow = this.confirmedWindowSource;
    if (!this.selecting && confirmedWindow && (confirmedWindow.processId !== snapshot.windowCapturePid
      || !sources.some(source => source.id === confirmedWindow.id))) this.confirmedWindowSource = null;
    // A legacy foreground selection publishes the same PID. Only our native
    // window acknowledgement plus completed preview activation proves selection.
    const selectedDisplay = displaySources.find(source => source.displayId === snapshot.selectedDisplayId)
      ?? displaySources.find(source => source.isPrimary)
      ?? displaySources[0];
    const selectedId = snapshot.windowCapturePid
      ? !this.selecting && this.confirmedWindowSource && this.selectedSource === this.confirmedWindowSource
        ? this.confirmedWindowSource.id
        : ''
      : snapshot.selectedDevice === 'display'
        ? selectedDisplay?.id ?? ''
        : snapshot.selectedDevice;
    return { sources, selectedId };
  }
}
